import type { ResolvedLocale } from '@visious/core';

/**
 * R7's other half, **bound to one locale**.
 *
 * Core returns `viewCount: number` and `lengthSeconds: number` and never a formatted string, precisely
 * so this file can exist per platform; `Intl` is banned in core for the same reason `Date` is. What 4e
 * changed is *which* locale reaches `Intl`.
 *
 * Until 4e every call here passed `undefined`, i.e. **the browser's** locale, while the copy around it
 * was English. The result was a sentence assembled from two languages — the playlist header read
 * "Updated vor 2 Wochen" and the settings page "Last agreed jetzt" on a German browser — and no amount
 * of translating fixes that, because the two halves came from different places. So the formatters are
 * now a value rather than a module: {@link createFormatters} takes the resolved locale
 * (`core/i18n/locale.ts` decides it, `strings/` carries the words for the same language) and the app
 * has one instance of it, handed to the models that need it.
 *
 * {@link formatDuration} stays a plain function, and that is not an oversight: `h:mm:ss` is not a
 * localised form. It is the shape YouTube emits, the shape `text/timecode.ts` parses, and the shape a
 * chapter list has to agree with — a locale-aware duration would make the description's clickable
 * `1:23` and the badge under the thumbnail disagree about the same instant.
 */

/**
 * `h:mm:ss` past an hour, `m:ss` below it — YouTube's own shape, and the one timecodes parse in.
 *
 * **Past a day it switches to `Nd h:mm:ss`, and that arrived from a real row rather than from caution.**
 * A channel's Live tab lists ended 24/7 broadcasts and their `lengthSeconds` is genuine: Lofi Girl's
 * two retired radios report 74 782 294 and 75 037 431 seconds. In `h:mm:ss` that is `20772:51:34`,
 * which is true, fits the badge, and conveys nothing — nobody reads twenty thousand hours. `866d
 * 0:51:34` is the same number said in a unit a reader has.
 *
 * The threshold is a day rather than something rounder because the form is only ever *worse* below it:
 * `1d 0:05:00` for a 24-hour stream is not an improvement on `24:05:00`, but by three digits of hours
 * it plainly is, and a day is the first honest place to draw the line. Nothing that parses a timecode
 * reads this back — `text/timecode.ts` parses what YouTube *emits*, and YouTube emits no day form.
 */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';

  const whole = Math.floor(totalSeconds);
  const days = Math.floor(whole / 86400);
  const hours = Math.floor((whole % 86400) / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const seconds = whole % 60;
  const ss = String(seconds).padStart(2, '0');

  if (days > 0) return `${days}d ${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
  if (hours === 0) return `${minutes}:${ss}`;
  return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
}

/**
 * A noun in as many forms as a language needs it, keyed by CLDR's plural categories.
 *
 * `other` is the only required one because it is the only one every language has; English fills in
 * `one` and stops, Polish would fill in `few` and `many`, Japanese neither. The catalogue writes these
 * (`strings/`) and {@link Formatters.plural} picks between them — which is why the *number* is what
 * gets asked, never the formatted string.
 */
export interface PluralForms {
  readonly zero?: string;
  readonly one?: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

export interface Formatters {
  /** The tag handed to every `Intl` constructor below. Exposed because two views display it. */
  readonly locale: string;

  /** `1.2M`, `13K`, `842`. Compact rather than grouped — a view count is a magnitude, not a figure. */
  count(value: number | null): string | null;

  /** The exact figure, for a `title` attribute — compact notation hides three orders of magnitude. */
  exactCount(value: number | null): string | null;

  /** "3 weeks ago", from an epoch. */
  published(epochSec: number | null, nowEpochSec: number): string | null;

  /** An absolute date for the `title` attribute — "3 weeks ago" is not a date you can look up. */
  absoluteDate(epochSec: number | null): string | null;

  /** An exact figure worth putting in a `title`, or `null`. See the implementation for why. */
  distinctExactTitle(label: string | null, exact: string | null): string | null;

  /** The right form of a noun for a count, chosen on the number. */
  plural(value: number, forms: PluralForms): string;

  /** A count and its noun together — `formatCount` for the number, {@link Formatters.plural} for the word. */
  countedNoun(value: number | null, forms: PluralForms): string | null;

  /** A region code in the UI language ("DE" → "Germany"), the code itself where CLDR has no name. */
  regionName(code: string): string;

  /** Locale-aware ordering for user-visible lists — the region dropdown sorts by name with it. */
  compareText(a: string, b: string): number;
}

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86_400;
const WEEK = 604_800;
const MONTH = 2_629_800; // average Gregorian month, so "12 months" and "1 year" agree
const YEAR = 31_557_600;

/**
 * Every locale-dependent form the app produces, for one locale.
 *
 * The four `Intl` objects are constructed once per locale rather than per call, which matters more than
 * it looks: a 40-card feed asks for a relative date, a compact count and an exact count on every card,
 * and each constructor walks CLDR data. They are the same objects for the life of the locale because
 * the locale is a boot-time decision (`strings/useCopy.ts` memoises this).
 */
export function createFormatters(locale: string): Formatters {
  /*
   * Compaction is CLDR's decision, not ours, and it is **not** uniform — measured across 20 locales:
   * `en` gives `15.4K` for 15 436 while `de` gives `15.436` and `de-AT` `15 436`, because German has no
   * short pattern below a million (`1,2 Mio.` is its first). That is correct German rather than a
   * missing feature, so nothing here forces a suffix; what 4e fixed is the *pairing* — the number is
   * now formatted in the same language as the sentence around it, so an English UI says `15.4K` to
   * everybody and a future German one says `15.436` to everybody.
   */
  const compact = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 });
  const grouped = new Intl.NumberFormat(locale);
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const absolute = new Intl.DateTimeFormat(locale, { dateStyle: 'long' });
  const plurals = new Intl.PluralRules(locale);
  const regions = new Intl.DisplayNames(locale, { type: 'region' });
  const collator = new Intl.Collator(locale);

  function count(value: number | null): string | null {
    return value === null || !Number.isFinite(value) ? null : compact.format(value);
  }

  function plural(value: number, forms: PluralForms): string {
    // `select` returns a CLDR category, and a catalogue only writes the ones its language uses — so a
    // miss falls through to `other`, which every language has. Indexing rather than switching keeps
    // that total without listing six arms that five languages never reach.
    const category = plurals.select(value) as keyof PluralForms;
    return forms[category] ?? forms.other;
  }

  return {
    locale,
    count,
    exactCount: (value) => (value === null || !Number.isFinite(value) ? null : grouped.format(value)),

    published: (epochSec, nowEpochSec) => {
      if (epochSec === null || !Number.isFinite(epochSec)) return null;

      const delta = epochSec - nowEpochSec;
      const magnitude = Math.abs(delta);

      const [unit, size]: [Intl.RelativeTimeFormatUnit, number] =
        magnitude < MINUTE ? ['second', 1]
        : magnitude < HOUR ? ['minute', MINUTE]
        : magnitude < DAY ? ['hour', HOUR]
        : magnitude < WEEK ? ['day', DAY]
        : magnitude < MONTH ? ['week', WEEK]
        : magnitude < YEAR ? ['month', MONTH]
        : ['year', YEAR];

      // `RelativeTimeFormat` rather than a hand-written table because the plural rules are per-language
      // and getting them wrong is the kind of thing that reads as sloppy in every locale but English.
      return relative.format(Math.round(delta / size), unit);
    },

    absoluteDate: (epochSec) =>
      epochSec === null || !Number.isFinite(epochSec) ? null : absolute.format(new Date(epochSec * 1000)),

    /**
     * An exact figure worth putting in a `title`, or `null` — compared on **digits**, not on the strings.
     *
     * `count` compacts per language and not every language compacts below a million: measured in a
     * browser, German gives `1877 → "1877"` and `15436 → "15.436"`, which is the grouped form. On such a
     * locale every count's tooltip restated its own label, on every card in a 40-card grid and on the
     * Watch page's view and like counts. `2208` against `2.208` is the same number differently
     * punctuated; `1,2 Mio.` against `1.234.567` is not, and keeps its tooltip.
     *
     * 4e narrowed when this fires rather than removing it: the two now always come from the same locale,
     * so the comparison is between two spellings of one number instead of between two conventions.
     */
    distinctExactTitle: (label, exact) => {
      if (exact === null) return null;
      const digits = (value: string): string => value.replace(/\D+/g, '');
      return label !== null && digits(label) === digits(exact) ? null : exact;
    },

    plural,

    /*
     * Pluralised on the **number** rather than on the formatted string, which is the whole reason this
     * exists: `count` compacts (`23.3M`), so a view holding only the label cannot tell 1 from 21, and
     * a browser pass produced "1 videos" on a playlist with one entry.
     */
    countedNoun: (value, forms) => {
      const label = count(value);
      return label === null || value === null ? null : `${label} ${plural(value, forms)}`;
    },

    // `of` throws a RangeError on a malformed code rather than declining — and answers the code
    // back for a well-formed one CLDR has no name for. Both fall back to the code: in a settings
    // dropdown a bare "ZZ" beats a crash, and the code column repeats it anyway.
    regionName: (code) => {
      try {
        return regions.of(code) ?? code;
      } catch {
        return code;
      }
    },

    compareText: collator.compare,
  };
}

/** The formatters for a resolved locale — the pairing this slice is about, in one call. */
export function formattersFor(locale: ResolvedLocale): Formatters {
  return createFormatters(locale.formatting);
}
